// Port of TrailMap/MapProviderRegistry.swift (TrailBivvyKit): the official map providers TrailBivvy knows, and the
// standard composite used by the app.
//
// Selection order (see CompositeMapProvider): swisstopo, then France (IGN), Austria (basemap.at), Germany (BKG
// TopPlusOpen), Spain (IGN España), Norway (Kartverket), USA (USGS), then the Europe-wide TopPlusOpen overview (online
// only, z ≤ 12, drawn over the fallback map), and finally OpenStreetMap everywhere else (web replacement of Apple Maps;
// online only, never stored).
//
// URL templates, ids, zooms, sizes and attribution strings are identical to Swift (verified there on 2026-09-28).
// Web: every host's CORS answer was checked on 2026-10-20 (src/map/tileHosts.ts) and is recorded on each descriptor;
// terms per provider are in src/map/PROVIDERS.md. `allowsOfflineStorage` is the app's recommended flag, not a legal
// conclusion: every provider's provenance is `pendingHumanReview`.

import type { DataProvenance, EpochMs } from '../core/models';
import type { HTTPClient } from '../net/httpTypes';
import type { ConnectivityProviding } from '../net/connectivity';
import { makeTileLayerDescriptor, type TileLayerDescriptor, type TileLayerDescriptorInit } from '../offline/TileLayerDescriptor';
import { CompositeMapProvider } from './CompositeMapProvider';
import type { FairUseLimiter } from './GeoAdminWildlifeRestZones';
import { OpenStreetMapProvider } from './OpenStreetMapProvider';
import { ProviderCoverage, type ProviderCoverageRegion } from './ProviderCoverage';
import { RegionalTileMapProvider } from './RegionalTileMapProvider';
import { SwisstopoMapProvider } from './SwisstopoMapProvider';
import { corsEnabledFor } from './tileHosts';
import { MapAttributions, noLocalProtectedAreas, type LocalProtectedAreas, type MapAttribution } from './TrailMapProvider';

/** Date the templates were verified (2026-09-28), stamped into the providers' provenance. */
const verifiedAt: EpochMs = 1_790_553_600_000;

function regionalLayer(init: Omit<TileLayerDescriptorInit, 'coverage' | 'coveragePolygons' | 'corsEnabled'>, region: ProviderCoverageRegion): TileLayerDescriptor {
  return makeTileLayerDescriptor({
    ...init,
    corsEnabled: corsEnabledFor(init.urlTemplate),
    coverage: ProviderCoverage.bounds(region),
    coveragePolygons: ProviderCoverage.polygons(region),
  });
}

function provenance(provider: string, dataset: string, source: string, license: string, attribution: string): DataProvenance {
  return {
    provider,
    dataset,
    sourceURL: source,
    license,
    attribution,
    fetchedAt: verifiedAt,
    isSynthetic: false,
    licenseReviewStatus: 'pendingHumanReview',
  };
}

const topPlusOpenTemplate = 'https://sgx.geodatenzentrum.de/wmts_topplus_open/tile/1.0.0/web/default/WEBMERCATOR/{z}/{y}/{x}.png';
const usgsAttribution = 'U.S. Geological Survey, National Geospatial Program';

let cachedLayers: Record<string, TileLayerDescriptor> | undefined;

/** Layers are built lazily (they decode the bundled coverage outlines on first use). */
function layers(): Record<string, TileLayerDescriptor> {
  if (cachedLayers) return cachedLayers;
  cachedLayers = {
    // Plan IGN v2 (Géoplateforme WMTS, KVP, matrix set PM, z0–19). Etalab Licence Ouverte 2.0; WMTS not subject to the
    // Géoplateforme rate limits.
    ignPlanV2: regionalLayer(
      {
        id: 'fr.ign.planignv2',
        urlTemplate:
          'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&STYLE=normal&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&FORMAT=image/png',
        fileExtension: 'png',
        minZoom: 0,
        maxZoom: 19,
        averageTileBytes: 45_000,
        attribution: 'IGN – Plan IGN v2',
        isOpaque: true,
        allowsOfflineStorage: true,
      },
      'FR',
    ),
    // IGN aerial photographs (BD ORTHO, JPEG, z0–19): satellite base; never packaged offline (size).
    ignOrthophotos: regionalLayer(
      {
        id: 'fr.ign.orthophotos',
        urlTemplate:
          'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=ORTHOIMAGERY.ORTHOPHOTOS&STYLE=normal&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&FORMAT=image/jpeg',
        fileExtension: 'jpeg',
        minZoom: 0,
        maxZoom: 19,
        averageTileBytes: 25_000,
        attribution: 'IGN – BD ORTHO',
        isOpaque: true,
        allowsOfflineStorage: true,
      },
      'FR',
    ),
    // basemap.at Geoland Basemap (PNG, google3857 z0–20; note the {y}/{x} order). CC BY 4.0.
    basemapAT: regionalLayer(
      {
        id: 'at.basemap.geolandbasemap',
        urlTemplate: 'https://mapsneu.wien.gv.at/basemap/geolandbasemap/normal/google3857/{z}/{y}/{x}.png',
        fileExtension: 'png',
        minZoom: 0,
        maxZoom: 20,
        averageTileBytes: 35_000,
        attribution: 'Datenquelle: basemap.at',
        isOpaque: true,
        allowsOfflineStorage: true,
      },
      'AT',
    ),
    // basemap.at orthophoto (JPEG, google3857; {y}/{x} order). Satellite base; never packaged offline.
    basemapATOrthophoto: regionalLayer(
      {
        id: 'at.basemap.orthofoto',
        urlTemplate: 'https://mapsneu.wien.gv.at/basemap/bmaporthofoto30cm/normal/google3857/{z}/{y}/{x}.jpeg',
        fileExtension: 'jpeg',
        minZoom: 0,
        maxZoom: 20,
        averageTileBytes: 25_000,
        attribution: 'Datenquelle: basemap.at',
        isOpaque: true,
        allowsOfflineStorage: true,
      },
      'AT',
    ),
    // IGN España MTN raster (JPEG, GoogleMapsCompatible; native max set to the verified z17). CC BY 4.0 ign.es.
    ignSpainMTN: regionalLayer(
      {
        id: 'es.ign.mtn',
        urlTemplate:
          'https://www.ign.es/wmts/mapa-raster?service=WMTS&request=GetTile&version=1.0.0&Format=image/jpeg&layer=MTN&style=default&tilematrixset=GoogleMapsCompatible&TileMatrix={z}&TileRow={y}&TileCol={x}',
        fileExtension: 'jpeg',
        minZoom: 0,
        maxZoom: 17,
        averageTileBytes: 20_000,
        attribution: 'CC BY 4.0 ign.es',
        isOpaque: true,
        allowsOfflineStorage: true,
      },
      'ES',
    ),
    // Kartverket topo (webmercator z0–18; {y}/{x} order). CC BY 4.0, "© Kartverket". Offline storage off: copying the
    // cache service's z12–20 (Geovekst) data needs the rights holders' permission.
    kartverketTopo: regionalLayer(
      {
        id: 'no.kartverket.topo',
        urlTemplate: 'https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png',
        fileExtension: 'png',
        minZoom: 0,
        maxZoom: 18,
        averageTileBytes: 50_000,
        attribution: '© Kartverket',
        isOpaque: true,
        allowsOfflineStorage: false,
      },
      'NO',
    ),
    // USGSTopo (ArcGIS tile service, JPEG, z0–16; {y}/{x} order). Public domain; exportTilesAllowed true.
    usgsTopo: regionalLayer(
      {
        id: 'us.usgs.topo',
        urlTemplate: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}',
        fileExtension: 'jpeg',
        minZoom: 0,
        maxZoom: 16,
        averageTileBytes: 30_000,
        attribution: usgsAttribution,
        isOpaque: true,
        allowsOfflineStorage: true,
      },
      'US',
    ),
    // USGSImageryTopo (JPEG, z0–16; {y}/{x} order). Public domain. Satellite base; never packaged offline.
    usgsImageryTopo: regionalLayer(
      {
        id: 'us.usgs.imagerytopo',
        urlTemplate: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryTopo/MapServer/tile/{z}/{y}/{x}',
        fileExtension: 'jpeg',
        minZoom: 0,
        maxZoom: 16,
        averageTileBytes: 40_000,
        attribution: usgsAttribution,
        isOpaque: true,
        allowsOfflineStorage: true,
      },
      'US',
    ),
  };
  return cachedLayers;
}

export const MapProviderRegistry = {
  // MARK: Provider ids

  franceID: 'ign-fr',
  austriaID: 'basemap-at',
  germanyID: 'bkg-topplusopen-de',
  spainID: 'ign-es',
  norwayID: 'kartverket-no',
  unitedStatesID: 'usgs-us',
  europeFallbackID: 'bkg-topplusopen-europe',

  // MARK: Layers

  get ignPlanV2(): TileLayerDescriptor {
    return layers().ignPlanV2;
  },
  get ignOrthophotos(): TileLayerDescriptor {
    return layers().ignOrthophotos;
  },
  get basemapAT(): TileLayerDescriptor {
    return layers().basemapAT;
  },
  get basemapATOrthophoto(): TileLayerDescriptor {
    return layers().basemapATOrthophoto;
  },
  get ignSpainMTN(): TileLayerDescriptor {
    return layers().ignSpainMTN;
  },
  get kartverketTopo(): TileLayerDescriptor {
    return layers().kartverketTopo;
  },
  get usgsTopo(): TileLayerDescriptor {
    return layers().usgsTopo;
  },
  get usgsImageryTopo(): TileLayerDescriptor {
    return layers().usgsImageryTopo;
  },

  /**
   * Source note required by the TopPlusOpen licence (from the WMTS capabilities `ows:Fees`): "Kartendarstellung: © BKG
   * (<year of the last data retrieval>) dl-de/by-2-0, Datenquellen: <sources page>".
   */
  topPlusOpenAttribution(dataYear: number): string {
    return `Kartendarstellung: © BKG (${dataYear}) dl-de/by-2-0, Datenquellen: https://sgx.geodatenzentrum.de/web_public/gdz/datenquellen/datenquellen_topplusopen.html`;
  },

  /** TopPlusOpen `web` for Germany (PNG, WEBMERCATOR z0–18; {y}/{x} order). dl-de/by-2-0; offline inside Germany. */
  topPlusOpenGermany(dataYear: number): TileLayerDescriptor {
    return regionalLayer(
      {
        id: 'de.bkg.topplusopen.web',
        urlTemplate: topPlusOpenTemplate,
        fileExtension: 'png',
        minZoom: 0,
        maxZoom: 18,
        averageTileBytes: 25_000,
        attribution: MapProviderRegistry.topPlusOpenAttribution(dataYear),
        isOpaque: true,
        allowsOfflineStorage: true,
      },
      'DE',
    );
  },

  /**
   * TopPlusOpen `web` as a Europe-wide overview, capped at z12 and online only (no offline packages beyond Germany).
   * Not opaque: drawn over the fallback base map, which takes over beyond z12.
   */
  topPlusOpenEurope(dataYear: number): TileLayerDescriptor {
    return regionalLayer(
      {
        id: 'eu.bkg.topplusopen.web.overview',
        urlTemplate: topPlusOpenTemplate,
        fileExtension: 'png',
        minZoom: 0,
        maxZoom: 12,
        averageTileBytes: 15_000,
        attribution: MapProviderRegistry.topPlusOpenAttribution(dataYear),
        isOpaque: false,
        allowsOfflineStorage: false,
      },
      'EUROPE',
    );
  },

  /** OpenStreetMap data inside TopPlusOpen (attribution as good practice). */
  openStreetMapAttribution: MapAttributions.openStreetMap as MapAttribution,

  // MARK: Providers

  /** France (metropolitan + Corsica): Plan IGN v2, IGN aerial photographs as satellite. */
  france(localProtectedAreas: LocalProtectedAreas = noLocalProtectedAreas): RegionalTileMapProvider {
    const l = layers();
    return new RegionalTileMapProvider({
      id: MapProviderRegistry.franceID,
      displayName: 'IGN Plan IGN v2',
      coverage: ProviderCoverage.bounds('FR'),
      coveragePolygons: ProviderCoverage.polygons('FR'),
      topographic: l.ignPlanV2,
      satellite: l.ignOrthophotos,
      attributionURL: 'https://www.ign.fr',
      provenance: [
        provenance(
          'IGN (Géoplateforme)',
          'Plan IGN v2 (WMTS GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2)',
          'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetCapabilities',
          'Licence Ouverte / Open Licence 2.0 (Etalab)',
          l.ignPlanV2.attribution,
        ),
        provenance(
          'IGN (Géoplateforme)',
          'Aerial photographs, BD ORTHO (WMTS ORTHOIMAGERY.ORTHOPHOTOS)',
          'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetCapabilities',
          'Licence Ouverte / Open Licence 2.0 (Etalab)',
          l.ignOrthophotos.attribution,
        ),
      ],
      localProtectedAreas,
    });
  },

  /** Austria: basemap.at base map and orthophoto. */
  austria(localProtectedAreas: LocalProtectedAreas = noLocalProtectedAreas): RegionalTileMapProvider {
    const l = layers();
    return new RegionalTileMapProvider({
      id: MapProviderRegistry.austriaID,
      displayName: 'basemap.at',
      coverage: ProviderCoverage.bounds('AT'),
      coveragePolygons: ProviderCoverage.polygons('AT'),
      topographic: l.basemapAT,
      satellite: l.basemapATOrthophoto,
      attributionURL: 'https://basemap.at',
      provenance: [
        provenance(
          'basemap.at (Austrian administrations, operated by the City of Vienna)',
          'Geoland Basemap and orthophoto (WMTS geolandbasemap, bmaporthofoto30cm)',
          'https://mapsneu.wien.gv.at/basemapneu/1.0.0/WMTSCapabilities.xml',
          'CC BY 4.0',
          l.basemapAT.attribution,
        ),
      ],
      localProtectedAreas,
    });
  },

  /** Germany: BKG TopPlusOpen (offline packages allowed inside Germany). */
  germany(dataYear: number, localProtectedAreas: LocalProtectedAreas = noLocalProtectedAreas): RegionalTileMapProvider {
    const layer = MapProviderRegistry.topPlusOpenGermany(dataYear);
    return new RegionalTileMapProvider({
      id: MapProviderRegistry.germanyID,
      displayName: 'BKG TopPlusOpen',
      coverage: ProviderCoverage.bounds('DE'),
      coveragePolygons: ProviderCoverage.polygons('DE'),
      topographic: layer,
      attributionURL: 'https://gdz.bkg.bund.de/index.php/default/wmts-topplusopen-wmts-topplus-open.html',
      additionalAttributions: [MapProviderRegistry.openStreetMapAttribution],
      provenance: topPlusOpenProvenance(layer.attribution),
      localProtectedAreas,
    });
  },

  /** Spain: IGN España Mapa Topográfico Nacional. */
  spain(localProtectedAreas: LocalProtectedAreas = noLocalProtectedAreas): RegionalTileMapProvider {
    const l = layers();
    return new RegionalTileMapProvider({
      id: MapProviderRegistry.spainID,
      displayName: 'IGN España MTN',
      coverage: ProviderCoverage.bounds('ES'),
      coveragePolygons: ProviderCoverage.polygons('ES'),
      topographic: l.ignSpainMTN,
      attributionURL: 'https://www.ign.es',
      provenance: [
        provenance(
          'Instituto Geográfico Nacional (IGN España)',
          'Mapa Topográfico Nacional raster (WMTS mapa-raster, layer MTN)',
          'https://www.ign.es/wmts/mapa-raster?request=GetCapabilities&service=WMTS',
          'CC BY 4.0',
          l.ignSpainMTN.attribution,
        ),
      ],
      localProtectedAreas,
    });
  },

  /** Norway: Kartverket topographic map (online only until Kartverket confirms offline storage). */
  norway(localProtectedAreas: LocalProtectedAreas = noLocalProtectedAreas): RegionalTileMapProvider {
    const l = layers();
    return new RegionalTileMapProvider({
      id: MapProviderRegistry.norwayID,
      displayName: 'Kartverket',
      coverage: ProviderCoverage.bounds('NO'),
      coveragePolygons: ProviderCoverage.polygons('NO'),
      topographic: l.kartverketTopo,
      attributionURL: 'https://www.kartverket.no',
      provenance: [
        provenance(
          'Kartverket',
          'Topographic map (WMTS topo, webmercator)',
          'https://cache.kartverket.no/v1/wmts/1.0.0/WMTSCapabilities.xml',
          'CC BY 4.0',
          l.kartverketTopo.attribution,
        ),
      ],
      localProtectedAreas,
    });
  },

  /** USA (50 states): USGS topo, USGS imagery with topo labels as satellite. */
  unitedStates(localProtectedAreas: LocalProtectedAreas = noLocalProtectedAreas): RegionalTileMapProvider {
    const l = layers();
    return new RegionalTileMapProvider({
      id: MapProviderRegistry.unitedStatesID,
      displayName: 'USGS The National Map',
      coverage: ProviderCoverage.bounds('US'),
      coveragePolygons: ProviderCoverage.polygons('US'),
      topographic: l.usgsTopo,
      satellite: l.usgsImageryTopo,
      attributionURL: 'https://www.usgs.gov/programs/national-geospatial-program/national-map',
      provenance: [
        provenance(
          'U.S. Geological Survey, The National Map',
          'USGSTopo and USGSImageryTopo (ArcGIS tile services)',
          'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer?f=json',
          'Public domain (U.S. Government work)',
          usgsAttribution,
        ),
      ],
      localProtectedAreas,
    });
  },

  /** Europe-wide overview: TopPlusOpen z ≤ 12 over the fallback base map, online only. */
  europeFallback(dataYear: number, localProtectedAreas: LocalProtectedAreas = noLocalProtectedAreas): RegionalTileMapProvider {
    const layer = MapProviderRegistry.topPlusOpenEurope(dataYear);
    return new RegionalTileMapProvider({
      id: MapProviderRegistry.europeFallbackID,
      displayName: 'BKG TopPlusOpen (Europe overview)',
      coverage: ProviderCoverage.bounds('EUROPE'),
      coveragePolygons: ProviderCoverage.polygons('EUROPE'),
      topographic: layer,
      attributionURL: 'https://gdz.bkg.bund.de/index.php/default/wmts-topplusopen-wmts-topplus-open.html',
      additionalAttributions: [MapProviderRegistry.openStreetMapAttribution],
      provenance: topPlusOpenProvenance(layer.attribution),
      tier: 'overview',
      localProtectedAreas,
    });
  },

  /**
   * The regional providers in selection order (national providers first, the Europe overview last). `now` gives the
   * year quoted in the TopPlusOpen source note.
   */
  regionalProviders(now: EpochMs, localProtectedAreas: LocalProtectedAreas = noLocalProtectedAreas): RegionalTileMapProvider[] {
    const year = MapProviderRegistry.dataYear(now);
    return [
      MapProviderRegistry.france(localProtectedAreas),
      MapProviderRegistry.austria(localProtectedAreas),
      MapProviderRegistry.germany(year, localProtectedAreas),
      MapProviderRegistry.spain(localProtectedAreas),
      MapProviderRegistry.norway(localProtectedAreas),
      MapProviderRegistry.unitedStates(localProtectedAreas),
      MapProviderRegistry.europeFallback(year, localProtectedAreas),
    ];
  },

  /**
   * The app's map provider: swisstopo (with online wildlife rest zones when `client` is given), the regional providers,
   * and OpenStreetMap as the final fallback.
   */
  standard(
    options: {
      client?: HTTPClient;
      localProtectedAreas?: LocalProtectedAreas;
      connectivity?: ConnectivityProviding;
      now?: () => EpochMs;
      limiter?: FairUseLimiter;
    } = {},
  ): CompositeMapProvider {
    const now = options.now ?? (() => Date.now());
    const local = options.localProtectedAreas ?? noLocalProtectedAreas;
    return new CompositeMapProvider(
      new SwisstopoMapProvider({
        client: options.client,
        localProtectedAreas: local,
        connectivity: options.connectivity,
        now,
        limiter: options.limiter,
      }),
      new OpenStreetMapProvider(local),
      MapProviderRegistry.regionalProviders(now(), local),
    );
  },

  /** Gregorian year of `date` in UTC. */
  dataYear(date: EpochMs): number {
    return new Date(date).getUTCFullYear();
  },
};

function topPlusOpenProvenance(attribution: string): DataProvenance[] {
  return [
    provenance(
      'BKG – Bundesamt für Kartographie und Geodäsie',
      'TopPlusOpen (WMTS web, WEBMERCATOR)',
      'https://sgx.geodatenzentrum.de/wmts_topplus_open/1.0.0/WMTSCapabilities.xml',
      'Datenlizenz Deutschland – Namensnennung – Version 2.0 (dl-de/by-2-0)',
      attribution,
    ),
    provenance(
      'OpenStreetMap contributors (inside TopPlusOpen)',
      'OpenStreetMap data rendered by BKG',
      'https://www.openstreetmap.org/copyright',
      'ODbL 1.0',
      MapAttributions.openStreetMap.text,
    ),
  ];
}
